import {
  Component,
  EventEmitter,
  Inject,
  Input,
  OnDestroy,
  OnInit,
  Optional,
  Output,
  ViewChild,
} from '@angular/core';
import { NZ_MODAL_DATA, NzModalRef, NzModalService } from 'ng-zorro-antd/modal';
import { assignModalData } from '../../../utils/modal-data.util';
import { NzUploadFile } from 'ng-zorro-antd/upload';
import { NotificatorService } from '../../../services/notificator.service';
import { ImageEditorService } from '../image.editor.service';
import { AdjustCfg, EventImage, WizardChapterData } from '../img.editor.iface';
import { WIZARD_CFG } from '../defaults.data';
import { VendorMinInf } from '../../../interfaces/common';
import { environment } from '../../../../../environments/environment';
import { CdkDragDrop, moveItemInArray } from '@angular/cdk/drag-drop';
import { AeVendorItemService } from '../../../../components/vendor.items/add.edit/ae.vendor.item.service';
import { PreloaderService } from '../../preloader/preloader.service';
import { Media } from '../../../../components/vendor.items/add.edit/steps/interfaces/s4';

@Component({
  selector: 'app-wizard-imager',
  templateUrl: 'wizard.component.html',
  styleUrls: ['wizard.component.css'],
  standalone: false,
})
export class WizardComponent implements OnInit, OnDestroy {
  @Input() images: EventImage[];
  @Input() gallery: EventImage[];
  @Input() settings: {
    vendor: VendorMinInf;
    entityType: 'event' | 'hotel' | string;
  };
  @Output() complete = new EventEmitter();
  // Type of images
  imagesDB: {
    cover: EventImage[];
    seat_map: EventImage[];
    gallery: EventImage[];
    unclassified: EventImage[];
    [key: string]: EventImage[];
  } = {
    cover: [],
    seat_map: [],
    gallery: [],
    unclassified: [],
  };

  // Defaults [>]
  coverMinX = 360;
  seatMapMinX = 1000;
  galleryMinX = 1400;
  // Defaults [/]

  // File Manager
  chapters = [
    {
      title: 'cover',
      required: true,
      tag: 'cover',
    },
    {
      title: 'Seat map',
      required: false,
      tag: 'seat_map',
    },
    {
      title: 'gallery',
      required: false,
      tag: 'gallery',
    },
    {
      title: 'unclassified',
      required: false,
      tag: 'unclassified',
    },
  ];
  // Use <classified> for filter
  classified = ['cover', 'cover-ads', 'cover-stripe', 'seat_map', 'gallery-big', 'gallery-thumb'];
  requiredTypes = ['cover', 'cover-ads']; // , 'cover-stripe']
  coverTypesExists = {
    cover: false,
    'cover-ads': false,
    'cover-stripe': false,
  };
  /** Snapshot of edited image metadata (id/tags/contentId). */
  editionFileSrc: Partial<EventImage> | null;
  editionFile: Partial<EventImage> | null;
  minSizes = (() => {
    const newObject: Record<string, number> = {};
    this.classified.forEach((key) => {
      newObject[key] = (WIZARD_CFG.types as Record<string, { minWidth: number }>)[key].minWidth;
    });
    return newObject;
  })();

  // Modes
  addMode = false;
  editMode = false;
  wizardMode = false;
  batchUploadMode = false;
  batchUploadProcess = false;

  // Internal Gallery Preloader
  spinnerActive: boolean;
  spinnerText: string;
  spinnerChapter: string;

  // Add / Edit Mode Variables
  currentTag: string;
  currentHandle: WizardChapterData | null = null; // WIZARD_CFG[config]

  // Wizard Mode Variables
  step = 0;
  globalUploaded: boolean;
  private defaultSteps = [
    WIZARD_CFG.types.cover,
    WIZARD_CFG.types['cover-ads'],
    WIZARD_CFG.types.seat_map,
    WIZARD_CFG.types['gallery-big'],
  ];
  private coverSteps = [WIZARD_CFG.types.cover, WIZARD_CFG.types['cover-ads']];
  // Dynamic steps (now is default)
  handleSteps: WizardChapterData[] = [...environment.deepClone2(this.defaultSteps)];

  calcRecommendation(
    item: EventImage,
    realX: number,
  ): [
    boolean, // realX <= minX (lock edit button)
    boolean, // realX is too less than minX
    string, // Recommendation
  ] {
    const type = item.tags[0];
    if (type === 'unclassified') {
      return [false, false, null];
    }
    const cfg = (WIZARD_CFG.types as Record<string, WizardChapterData>)[type] as WizardChapterData;
    if (realX <= cfg.minWidth) {
      if (cfg.minWidth - realX > cfg.minWidth - cfg.minUploadWidth) {
        return [
          true,
          true,
          'Recommendation: Delete this image and upload a new one with a larger size.',
        ];
      } else {
        return [
          true,
          false,
          'You cannot edit this image because it already has the minimum dimensions.',
        ];
      }
    } else {
      return [false, false, null];
    }
  }

  // Drag'n'drop
  drop(event: CdkDragDrop<EventImage[]>, chapter: string) {
    console.log('dropped: ', event);
    this.spinnerActive = true;
    this.spinnerChapter = 'gallery';
    this.spinnerText = 'Saving sorting...';
    moveItemInArray(this.imagesDB[chapter], event.previousIndex, event.currentIndex);
    // save sorting
    const sortToSave = this.imagesDB[chapter].map((i: EventImage) => i.id);
    this.VendorService.saveSorting(sortToSave).then(
      (res) => {
        this.notify.createTopMessage('Sorting Saved', 'success', 1000);
        this.spinnerActive = false;
      },
      (error) => {
        this.spinnerActive = false;
        this.notify.createTopMessage('Can not save Sorting, server sent error', 'error', 5000);
      },
    );
    // this.modal.updateConfig({
    //   nzTitle: 'New Modal Title'
    // })
  }

  // currentDescription: string
  // imageExists: boolean
  // Editor wizard [begin]

  syncOutputData() {
    this.complete.emit(this.imagesDB);
    // this.complete.emit({
    //   cover: this.images.filter(i => i.tags.join().indexOf('cover') >= 0),
    //   seat_map: this.images.filter(i => i.tags.join().indexOf('seat_map') >= 0),
    //   gallery: this.images.filter(i => i.tags.join().indexOf('gallery') >= 0),
    //   unclassified: this.images.filter(i => {
    //     return this.classified.indexOf(i.tags[0]) < 0
    //   })
    // })
  }

  /**
   * CALLBACK FROM EDITOR
   * After Each user-event - translate updated data
   * @param e - {image: base64, width, height}
   * @param item: WizardChapterData
   */
  saveOutput(
    e: { image: string | null; width?: number; height?: number },
    item: WizardChapterData,
  ) {
    console.log('crop output: ', e, item);
    if (this.wizardMode) {
      item.imageExists = true;
      if (e.image !== null) {
        item.config.adjustedSrc = e.image;
        item.config.outputSize = {
          x: e.width,
          y: e.height,
        };
      }
    } else {
      // Add / Edit modes
      item.imageExists = true;
      if (e.image !== null) {
        this.currentHandle.config.adjustedSrc = e.image;
        // item.config.adjustedSrc = e.image
        // item.config.outputSize = {
        //   x: e.width,
        //   y: e.height
        // }
      }
    }
  }

  // Go to previous step
  prev() {
    this.step--;
  }

  // Go to Next step
  next(isSkip?: boolean) {
    console.log('got output (next): ', this.handleSteps);
    if (isSkip) {
      this.handleSteps[this.step].config = {
        source: null,
        imageId: null,
        adjustedSrc: null,
        skipped: true,
        originSize: {
          x: null,
          y: null,
        },
        outputSize: {
          x: null,
          y: null,
        },
      };
    } else {
      this.handleSteps[this.step].config.skipped = false;
    }
    this.step++;
  }

  /**
   * Finist step
   * remove previous image
   * save all adjusted images
   */
  async finish() {
    console.log('got output: ', this.handleSteps);
    console.log('edition: ', this.editionFile);
    console.log('ctrl: ', this);
    // this.addMode = false
    // this.editMode = false
    // const blob = await this.editor.getOutput()
    // console.log('finish got output: ', {blob})

    const resetWizardCache = () => {
      this.currentTag = null;
      this.currentHandle = null;
      this.globalUploaded = false;
      this.IES.setFile(null);
      this.handleSteps = environment.deepClone2(this.defaultSteps);
    };

    const remove = (batch: number[]): Promise<boolean[]> => {
      return new Promise((resolve, reject) => {
        this.IES.removeBatch(
          batch,
          this.settings.vendor.category,
          this.settings.vendor.vendorId,
        ).then(
          (res) => {
            console.log('removed batch files: ', res);
            resolve(res);
          },
          (error) => {
            reject(error);
          },
        );
      });
    };
    const save = (images: { img: string; tags: string[] }[]): Promise<Media[]> => {
      return new Promise((resolve, reject) => {
        this.IES.addImageBatch(
          images,
          this.settings.vendor.category,
          this.settings.vendor.vendorId,
        ).then(
          (res) => {
            console.log('Added batch files: ', res);
            resolve(res);
          },
          (error) => {
            reject(error);
          },
        );
      });
    };

    if (this.addMode) {
      // ADD MODE
      console.log('--AM-before save: ', this.handleSteps, this.currentTag);
      this.IES.resizeImageBase64(
        this.currentHandle.config.adjustedSrc,
        this.currentHandle.minWidth,
        +this.currentHandle.ratio.split(':')[0] / +this.currentHandle.ratio.split(':')[1],
      ).then((interpolated) => {
        const TS = [
          {
            img: interpolated,
            tags: [this.currentHandle.tag],
          },
        ];
        console.log('--TS: ', TS);
        save(TS).then(
          (added) => {
            console.log('--ADDED new images: ', added);
            // this.imagesDB[this.currentTag].push(added)
            this.imagesDB[this.currentTag] = [
              ...this.imagesDB[this.currentTag],
              ...(added as EventImage[]),
            ];
            // this.images = [...this.images, ...added]
            this.addMode = false;
            resetWizardCache();
            this.syncOutputData();
            this.notify.createTopMessage('Image has been added successfully', 'success', 2000);
          },
          (error) => {
            console.log('error add files: ', error);
            this.notify.createTopMessage(
              'Server sent error. Can not save Image file',
              'error',
              3500,
            );
          },
        );
      });
    } else if (this.editMode) {
      // EDIT MODE

      // step 1 remove previously file(s)
      let filesToRemove = [this.editionFile.id];
      if (this.editionFile.tags.indexOf('unclassified') >= 0) {
        this.imagesDB.unclassified = this.imagesDB.unclassified.filter((f) => {
          return f.id !== this.editionFile.id;
        });
        if (this.currentTag === 'seat_map') {
          const seatMaps = this.imagesDB.seat_map.map((i) => i.id);
          filesToRemove = [...filesToRemove, ...seatMaps];
        }
      } else {
        if (this.currentTag === 'cover') {
          const covers = this.imagesDB[this.currentTag].map((i) => i.id);
          filesToRemove = [...filesToRemove, ...covers];
        }
      }

      // step 1 - 2 Add cropped files (after remove prev)
      remove(filesToRemove).then(
        (done) => {
          this.imagesDB[this.currentTag] = this.imagesDB[this.currentTag].filter(
            (f: EventImage) => {
              return filesToRemove.indexOf(f.id) < 0;
            },
          );
          this.IES.resizeImageBase64(
            this.currentHandle.config.adjustedSrc,
            this.currentHandle.minWidth,
            +this.currentHandle.ratio.split(':')[0] / +this.currentHandle.ratio.split(':')[1],
          ).then((interpolated) => {
            save([
              {
                img: interpolated,
                tags: [this.currentHandle.tag],
              },
            ]).then(
              (saved) => {
                console.log('---SAVED: ', saved);

                const finishHandle = () => {
                  this.imagesDB[this.currentTag] = [
                    ...(this.currentTag === 'cover'
                      ? this.imagesDB[this.currentTag].filter((i) => i.tags.indexOf('cover') < 0)
                      : this.imagesDB[this.currentTag]),
                    ...(saved as EventImage[]),
                  ];
                  // this.images = [...this.images, ...saved]
                  this.editMode = false;
                  resetWizardCache();
                  this.syncOutputData();
                  this.notify.createTopMessage(
                    'Image has been adjusted successfully',
                    'success',
                    2500,
                  );
                };

                if (this.currentTag === 'cover') {
                  // видалення попереднього зображення cover якщо воно є
                  const prevCover = this.imagesDB[this.currentTag].filter(
                    (i) => i.tags.indexOf('cover') >= 0,
                  );
                  if (prevCover.length > 0) {
                    remove([prevCover[0].id]).then((ok) => {
                      finishHandle();
                    });
                  } else {
                    finishHandle();
                    this.editMode = false;
                  }
                } else {
                  finishHandle();
                  this.editMode = false;
                }
              },
              (error) => {
                console.log('---error save: ', error);
                this.notify.createTopMessage(
                  'Server sent error... Can not save image file',
                  'error',
                  3500,
                );
              },
            );
          });
        },
        (error) => {
          console.log('---error remove');
          this.notify.createTopMessage(
            'Server sent error... Can not remove previously image files',
            'error',
            3500,
          );
        },
      );
    } else if (this.wizardMode) {
      // const TS = this.handleSteps.map(i => {
      //   return {
      //     img: i.config.adjustedSrc,
      //     tags: [i.tag]
      //   }
      // })

      const tasks = this.handleSteps
        .filter((i) => i.config.adjustedSrc !== null)
        .map((i) => {
          return this.IES.resizeImageBase64(
            i.config.adjustedSrc,
            i.minWidth,
            +i.ratio.split(':')[0] / +i.ratio.split(':')[1],
            [i.tag],
          );
        });
      const resized = await Promise.all(tasks);
      console.log('RESIZED: ', resized);

      // return
      // console.log('wizard to save: ', TS)
      this.preloader.turnOnPreloader();
      this.preloader.printMessage('Saving images...');
      save(resized).then(
        (saved) => {
          saved.forEach((img) => {
            const eventImg = img as EventImage;
            switch (img.tags[0]) {
              case 'cover':
              case 'cover-ads':
              case 'cover-stripe':
                this.imagesDB.cover.push(eventImg);
                break;
              case 'seat_map':
                this.imagesDB.seat_map.push(eventImg);
                break;
              case 'gallery':
              case 'gallery-big':
              case 'gallery-thumb':
                this.imagesDB.gallery.push(eventImg);
                break;
            }
          });
          this.notify.createModalNotification(
            'Complete.',
            'All images have been successfully uploaded. You can now upload more images to the gallery using the file manager.',
            'info',
          );
          this.preloader.turnOffPreloader();
          resetWizardCache();
          this.syncOutputData();
          this.wizardMode = false;
          this.batchUploadProcess = false;
          console.log('saved: ', saved);
        },
        (error) => {
          this.preloader.turnOffPreloader();
          this.notify.createNotification(
            'error',
            'Server sent error',
            'Unable to upload images, try again later',
            5,
          );
          console.log('error:', error);
        },
      );
    }
  }

  cancel() {
    if (this.batchUploadProcess) {
      this.wizardMode = false;
      this.batchUploadMode = true;
      return;
    }
    this.addMode = false;
    this.editMode = false;

    this.imagesDB[this.currentTag] = this.imagesDB[this.currentTag].map((m: EventImage) => {
      if (this.editionFileSrc?.id) {
        if (m.id === this.editionFileSrc?.id) {
          m.contentId = this.editionFileSrc.contentId;
          m.tags = this.editionFileSrc.tags;
        }
      }
      return m;
    });
    // this.images = this.images.map(m => {
    //   if (this.editionFileSrc?.id) {
    //     if (m.id === this.editionFileSrc?.id) {
    //       m.contentId = this.editionFileSrc.contentId
    //       m.tags = this.editionFileSrc.tags
    //     }
    //   }
    //   return m
    // })
    this.editionFile = null;
    this.editionFileSrc = null;
    this.IES.setFile(null);
  }

  /**
   * Global uploader
   * Upload one image for all opened editors
   */
  upload(event: { file: NzUploadFile; type: string }, tag: string) {
    if (tag) {
      this.currentTag = tag;
    }
    // const image = new Image()

    // get minimal size
    let minImageWidth;
    let minImageWidthToResize;
    if (this.wizardMode) {
      minImageWidth = this.handleSteps[this.step].minWidth;
      minImageWidthToResize = this.handleSteps[this.step].minUploadWidth;
    } else {
      minImageWidth = this.currentHandle.minWidth;
      minImageWidthToResize = this.currentHandle.minUploadWidth;
    }

    console.log('chosen file: ', event);
    let file = null;
    if (event) {
      if (event.type === 'start') {
        file = event.file!.originFileObj!;
      } else {
        return;
      }
    }
    if (!file) {
      return;
    }

    // call upload image service
    this.IES.uploadImage(event, {
      minSize: minImageWidth,
      minSizeToResize: minImageWidthToResize,
    }).then(
      (img: HTMLImageElement) => {
        console.log('UPLOADED: ', img);
        this.IES.setFile(img.src);
        this.globalUploaded = true;
        if (this.wizardMode) {
          this.handleSteps.forEach((i) => {
            i.imageExists = true;
          });
        } else {
          this.currentHandle.imageExists = true;
        }
      },
      (error) => {
        console.log('REJECTED');
      },
    );
  }

  // upload(event: { file: NzUploadFile, type: string }, tag) {
  //   if (tag) {
  //     this.currentTag = tag
  //   }
  //   const image = new Image()
  //
  //   // get minimal size
  //   let minImageWidth
  //   let minImageWidthToResize
  //   if (this.wizardMode) {
  //     minImageWidth = this.handleSteps[this.step].minWidth
  //     minImageWidthToResize = this.handleSteps[this.step].minUploadWidth
  //   } else {
  //     minImageWidth = this.currentHandle.minWidth
  //     minImageWidthToResize = this.currentHandle.minUploadWidth
  //   }
  //
  //   console.log('chosen file: ', event)
  //   let file = null
  //   if (event) {
  //     if (event.type === 'start') {
  //       file = event.file!.originFileObj!
  //     } else {
  //       return
  //     }
  //   }
  //   if (!file) {
  //     return
  //   }
  //   const reader = new FileReader()
  //   reader.onload = () => {
  //     const result = reader.result
  //     // console.log('file loaded: ', result)
  //     if (typeof result !== 'string') {
  //       return
  //     }
  //     image.src = result
  //     image.onload = () => {
  //       // Check uploaded image on min size
  //       if (image.width < minImageWidth && image.width >= minImageWidthToResize) {
  //         this.notify.confirmModal('Do resize?',
  //           'The uploaded image is smaller than the standard width, but the image has the minimum allowed size deviation. Resize?',
  //           {ok: 'Yes', cancel: 'No'}).then(yes => {
  //
  //           // Resizing
  //           let ratio
  //           if (this.wizardMode) {
  //             ratio = this.IES.calcRatio(this.handleSteps[this.step].ratio)
  //           } else {
  //             ratio = this.IES.calcRatio(this.currentHandle.ratio)
  //           }
  //           this.IES.resizeImageBase64(image.src, minImageWidth, ratio).then(newImg => {
  //             const newImage = new Image()
  //             newImage.src = newImg
  //             newImage.onload = () => {
  //               console.log('resized: ', newImage.width)
  //               this.IES.setFile(newImage.src)
  //               if (this.wizardMode) {
  //                 this.handleSteps.forEach(i => {
  //                   i.imageExists = true
  //                 })
  //               } else {
  //                 this.currentHandle.imageExists = true
  //               }
  //             }
  //           }, error => {
  //             this.notify.createTopMessage('Unable to resize this image', 'error', 2000)
  //           })
  //           return
  //         }, no => {
  //           // no resize
  //           return
  //         })
  //         return
  //       } else {
  //         if (image.width < minImageWidth) {
  //           this.notify.createModalNotification('Incompatible size',
  //             `You Uploaded image with size: ${image.width}x${image.height}px. but it's too small for this image-type.\nMinimal image width should be ', ${minImageWidth}px)`,
  //             'error')
  //           return
  //         } else {
  //           // no errors - load image
  //           // If no errors - load image as global
  //           this.IES.setFile(image.src, this.wizardMode)
  //           this.globalUploaded = true
  //           if (this.wizardMode) {
  //             // this.handleSteps[this.currentTag].forEach(i => {
  //             //   i.imageExists = true
  //             // })
  //           } else {
  //             this.currentHandle.imageExists = true
  //           }
  //         }
  //         console.log('load image: ', image.width, minImageWidth)
  //       }
  //     }
  //     // Send image to editor
  //     // this.editor.onFileSelected(null, null, result)
  //     // this.editionFile?.contentId = image.src
  //   }
  //   reader.readAsDataURL(file)
  // }

  // Editor wizard [end]

  uploadBatchInit() {
    this.handleSteps = [];
    this.step = 0;
  }

  removeFromBatch(index: number) {
    this.handleSteps = this.handleSteps.filter((i, idx) => idx !== index);
  }

  async gotoWizard() {
    this.preloader.turnOnPreloader();
    this.preloader.printMessage('Preparing images...');
    this.editionFile = null;
    const images = await Promise.all(
      this.handleSteps
        .filter((i) => {
          return i.config.originSize.x >= i.minUploadWidth;
        })
        .map(async (i) => {
          // const ratio = +i.ratio.split(':')[0] / +i.ratio.split(':')[1]
          const ratio = i.config.originSize.x / i.config.originSize.y;
          const base64 = await this.IES.resizeImageBase64(i.config.source, i.minWidth, ratio, [
            i.tag,
          ]);
          i.config.adjustedSrc = base64.img;
          return i;
        }),
    );
    console.log('PREPARED: ', images);
    this.handleSteps = images;
    this.batchUploadMode = false;
    this.wizardMode = true;
    this.preloader.turnOffPreloader();
  }

  uploadBatchCancel() {
    this.batchUploadMode = false;
    this.batchUploadProcess = false;
    this.step = 0;
  }

  uploadBatch(
    event: { file: NzUploadFile; fileList: NzUploadFile[]; type: string },
    type?: string,
  ) {
    if (event.type !== 'start') {
      return;
    }
    if (type) {
      this.currentTag = type;
    }
    const cfg = (WIZARD_CFG.types as Record<string, WizardChapterData>)[type];
    console.log('upload batch: ', event);

    // event.fileList.forEach((item, index) => {

    const file = event.file!.originFileObj;
    const reader = new FileReader();

    reader.onload = () => {
      const result = reader.result;
      // console.log('file loaded: ', result)
      if (typeof result !== 'string') {
        return;
      }
      const image = new Image();
      image.src = result;
      image.onload = () => {
        this.handleSteps.push({
          ...cfg,
          config: {
            source: image.src,
            imageId: null,
            adjustedSrc: image.src,
            skipped: false,
            originSize: {
              x: image.width,
              y: image.height,
            },
            outputSize: {
              x: image.width,
              y: image.height,
            },
          },
        });
        this.batchUploadMode = true;
        this.batchUploadProcess = true;
      };
    };
    reader.readAsDataURL(file);
    // })

    // const image = new Image()

    // get minimal size
    // let minImageWidth
    // let minImageWidthToResize
    // if (this.wizardMode) {
    //   minImageWidth = this.handleSteps[this.step].minWidth
    //   minImageWidthToResize = this.handleSteps[this.step].minUploadWidth
    // } else {
    //   minImageWidth = this.currentHandle.minWidth
    //   minImageWidthToResize = this.currentHandle.minUploadWidth
    // }
    //
    // console.log('chosen file: ', event)
    // let file = null
    // if (event) {
    //   if (event.type === 'start') {
    //     file = event.file!.originFileObj!
    //   } else {
    //     return
    //   }
    // }
    // if (!file) {
    //   return
    // }
    //
    // // call upload image service
    // this.IES.uploadImage(event, {
    //   minSize: minImageWidth,
    //   minSizeToResize: minImageWidthToResize
    // }).then((img: HTMLImageElement) => {
    //   console.log('UPLOADED: ', img)
    //   this.IES.setFile(img.src)
    //   this.globalUploaded = true
    //   if (this.wizardMode) {
    //     this.handleSteps.forEach(i => {
    //       i.imageExists = true
    //     })
    //   } else {
    //     this.currentHandle.imageExists = true
    //   }
    // }, error => {
    //   console.log('REJECTED')
    // })
  }

  addImage(type: string, configName?: string, asBatch?: boolean) {
    this.step = 0;
    this.addMode = true;

    if (configName) {
      if (asBatch) {
        // load images
        // make images array
        // make handlers
        this.currentTag = type;
        return;
      } else {
        this.currentHandle = (WIZARD_CFG.types as Record<string, WizardChapterData>)[configName];
      }
      console.log('loaded config for ', configName, this.currentHandle);
    } else {
      this.currentHandle = null;
    }
    this.currentTag = type;
    this.editionFile = {
      contentId: null,
    };

    // switch (type) {
    //   case 'cover':
    //     this.currentTag = 'cover'
    //     this.editionFile = {
    //       contentId: null
    //     }
    //     break
    //   // case 'cover-ads':
    //   //   this.currentTag = 'cover-ads'
    //   //   this.editionFile = {
    //   //     contentId: null
    //   //   }
    //   //   break
    //   case 'seat_map':
    //     this.currentTag = 'seat_map'
    //     this.editionFile = {
    //       contentId: null
    //     }
    //     break
    //   case 'gallery':
    //     this.currentTag = 'gallery'
    //     this.editionFile = {
    //       contentId: null
    //     }
    //     break
    // }
    // this.handleSteps[this.currentTag][this.step].imageExists = true
  }

  /**
   * When click [Edit image]
   * @param item: EventImage
   * @param manageAs - tag
   */
  async manage(
    item: EventImage,
    manageAs?: 'cover' | 'seat_map' | 'gallery' | 'gallery-big' | string,
  ) {
    console.log('open in editor: ', item, manageAs);
    this.step = 0;
    this.editionFileSrc = environment.deepClone2(item);
    this.editionFile = item;
    this.currentTag = manageAs.indexOf('gallery') >= 0 ? 'gallery' : manageAs;
    if (manageAs) {
      item.tags = [manageAs, 'unclassified'];
    } else {
    }
    // get tag
    // if (item.tags.join().indexOf('cover') >= 0) {
    //   item.tags = ['cover', ...item.tags]
    //   this.currentTag = 'cover'
    // } else if (item.tags.join().indexOf('seat_map') >= 0) {
    //   item.tags = ['seat_map', ...item.tags]
    //   this.currentTag = 'seat_map'
    // } else if (item.tags.join().indexOf('gallery') >= 0) {
    //   item.tags = ['gallery', ...item.tags]
    //   this.currentTag = 'gallery'
    // }

    // this.handleSteps[this.currentTag][this.step].imageExists = true

    let base64image = item.contentId;
    const params = (WIZARD_CFG.types as Record<string, WizardChapterData>)[manageAs];
    if (item.contentId.length < 100) {
      this.IES.getImageAsBase64(item.contentId).subscribe((base64) => {
        console.log('converted: ', { base64 });
        base64image = base64;
        this.currentHandle = {
          ...(WIZARD_CFG.types as Record<string, WizardChapterData>)[manageAs],
          base64: base64image,
          imageExists: true,
          tags: [manageAs],
        } as WizardChapterData;
        this.editMode = true;
      });
      return;
    }

    this.currentHandle = {
      ...(WIZARD_CFG.types as Record<string, WizardChapterData>)[manageAs],
      contentId: base64image,
      imageExists: true,
      tags: [manageAs],
    } as WizardChapterData;
    this.editMode = true;
    console.log('MANAGE: ', this.currentHandle);
    // [this.currentTag][this.step].imageExists = true
    // this.currentDescription = 'Chose Image for Cover with minimal width 2000px and set borders manually'
  }

  remove(item: EventImage, chapter: string) {
    console.log('---remove-item: ', item);
    this.spinnerActive = true;
    this.spinnerChapter = chapter;
    this.spinnerText = 'Removing Image';

    if (item.tags.join().indexOf('gallery')) {
      const ids = [item.id];
      this.IES.removeBatch(ids, this.settings.vendor.category, this.settings.vendor.vendorId).then(
        (ok) => {
          this.imagesDB[chapter] = this.imagesDB[chapter].filter(
            (i: EventImage) => i.id !== item.id,
          );
          this.notify.createTopMessage('Image has been removed successfully', 'success', 2000);
          this.syncOutputData();
          this.spinnerActive = false;
        },
        (error) => {
          console.log('error delete file: ', error);
          this.notify.createTopMessage(
            'Server sent error. Can not remove image file',
            'success',
            2000,
          );
          this.spinnerActive = false;
        },
      );
    } else {
      this.IES.removeImage(
        item.id,
        this.settings.vendor.category,
        this.settings.vendor.vendorId,
      ).then(
        (ok) => {
          this.imagesDB[chapter] = this.imagesDB[chapter].filter(
            (i: EventImage) => i.id !== item.id,
          );
          this.notify.createTopMessage('Image has been removed successfully', 'success', 2000);
          this.syncOutputData();
          this.spinnerActive = false;
        },
        (error) => {
          console.log('error delete file: ', error);
          this.notify.createTopMessage(
            'Server sent error. Can not remove image file',
            'success',
            2000,
          );
          this.spinnerActive = false;
        },
      );
    }
  }

  closeModal() {
    this.modal.close();
  }

  constructor(
    private modalService: NzModalService,
    private modal: NzModalRef,
    private preloader: PreloaderService,
    private VendorService: AeVendorItemService,
    private notify: NotificatorService,
    private IES: ImageEditorService,
    @Optional() @Inject(NZ_MODAL_DATA) modalData?: Partial<WizardComponent>,
  ) {
    assignModalData(this, modalData);
  }

  ngOnInit() {
    console.log('wizard-init: ', this);
    this.imagesDB = {
      cover: [],
      seat_map: [],
      gallery: [],
      unclassified: [],
    };
    if (this.images?.length > 0 || this.gallery?.length > 0) {
      this.images.forEach((img) => {
        // check on 'cover', 'cover-ads', 'cover-stripe'
        if (['cover', 'cover-ads', 'cover-stripe'].indexOf(img.tags[0]) >= 0) {
          this.imagesDB.cover.push(img);
        } else if (['seat_map'].indexOf(img.tags[0]) >= 0) {
          this.imagesDB.seat_map.push(img);
          // } else if (['gallery', 'gallery-big', 'gallery-thumb'].indexOf(img.tags[0]) >= 0) {
          //   this.imagesDB.gallery.push(img)
        } else {
          this.imagesDB.unclassified.push(img);
        }
      });
      if (this.gallery) {
        this.imagesDB.gallery = this.gallery.filter((img) => {
          if (img.tags.indexOf('gallery-big') >= 0) {
            return true;
          } else if (img.tags.indexOf('cover-ads') >= 0) {
            return false;
          } else if (img.tags.indexOf('none') >= 0) {
            this.imagesDB.unclassified.push({ ...img, tags: ['unclassified'] });
            return false;
          }
          return false;
        });
      }
    } else {
      this.wizardMode = true;
    }
  }

  ngOnDestroy() {
    this.IES.setFile(null);
  }
}
